import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

let syncInFlight = false;
let syncQueued = false;

function getRewardsConfig() {
  // Prefer the drawer bar when both exist, but fall back to the cart-page bar. The old
  // ["true"]-only selector meant this module was inert on /cart, so crossing the gift
  // threshold there updated the bar text but never added the gift until a page reload.
  const rewardsBar =
    document.querySelector('[data-cart-rewards-bar][data-drawer-rewards="true"]') ||
    document.querySelector('[data-cart-rewards-bar]');
  if (!(rewardsBar instanceof HTMLElement)) return null;

  const giftVariantId = Number(rewardsBar.dataset.giftVariantId || 0);
  const giftThresholdCents = Number(rewardsBar.dataset.giftThresholdCents || 0);

  if (!giftVariantId || !giftThresholdCents) return null;

  const giftLabel = `Free ${rewardsBar.dataset.giftName || 'Gift'}`;

  return { giftVariantId, giftThresholdCents, giftLabel };
}

function hasFreeGiftProperty(item) {
  return item?.properties?._free_gift === 'true';
}

function isGiftVariantLine(item, config) {
  return Number(item?.variant_id || item?.id || 0) === config.giftVariantId;
}

function isGiftLine(item, config) {
  return hasFreeGiftProperty(item) || isGiftVariantLine(item, config);
}

function getGiftLineItems(cart, config) {
  return (cart.items || []).filter((item) => isGiftLine(item, config));
}

function getPreferredGiftLine(giftLines) {
  return giftLines.find(hasFreeGiftProperty) || giftLines[0] || null;
}

function getCartItemsSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

function getQualifyingSubtotal(cart, config) {
  return (cart.items || []).reduce((subtotal, item) => {
    if (isGiftLine(item, config)) return subtotal;
    return subtotal + Number(item.final_line_price || 0);
  }, 0);
}

function morphReturnedSections(sections) {
  if (!sections) return;

  for (const [sectionId, sectionHtml] of Object.entries(sections)) {
    if (!sectionHtml) continue;

    try {
      morphSection(sectionId, sectionHtml);
    } catch (error) {
      console.error(`Unable to morph cart rewards section ${sectionId}`, error);
    }
  }
}

async function changeGiftLine(config, giftLineKey, quantity) {
  const sectionIds = getCartItemsSectionIds();

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      sections: sectionIds.join(',') || undefined,
      sections_url: window.location.pathname,
      id: giftLineKey,
      quantity,
    }),
  });

  if (!response.ok) throw new Error(`Cart reward change failed: ${response.status}`);

  const data = await response.json();
  morphReturnedSections(data.sections);
}

async function normalizeGiftLines(cart, config) {
  const giftLines = getGiftLineItems(cart, config);
  if (!giftLines.length) return null;

  const keptLine = getPreferredGiftLine(giftLines);

  for (const line of giftLines) {
    if (!line?.key) continue;

    if (line.key !== keptLine.key) {
      await changeGiftLine(config, line.key, 0);
    } else if (Number(line.quantity || 0) !== 1) {
      await changeGiftLine(config, line.key, 1);
    }
  }

  return keptLine;
}

async function removeFreeGiftLines(cart, config) {
  const giftLines = getGiftLineItems(cart, config).filter(hasFreeGiftProperty);
  let removed = false;

  for (const line of giftLines) {
    if (!line?.key) continue;

    await changeGiftLine(config, line.key, 0);
    removed = true;
  }

  return removed;
}

async function addGift(config) {
  const latestCart = await fetchCart();

  if (getGiftLineItems(latestCart, config).length) {
    await normalizeGiftLines(latestCart, config);
    return;
  }

  const sectionIds = getCartItemsSectionIds();
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      sections: sectionIds.join(',') || undefined,
      sections_url: window.location.pathname,
      id: config.giftVariantId,
      quantity: 1,
      properties: { _free_gift: 'true', Gift: config.giftLabel },
    }),
  });

  if (!response.ok) throw new Error(`Cart reward add failed: ${response.status}`);

  const data = await response.json();
  morphReturnedSections(data.sections);

  await normalizeGiftLines(await fetchCart(), config);
}

async function syncRewards() {
  if (syncInFlight) {
    syncQueued = true;
    return;
  }

  syncInFlight = true;

  try {
    const config = getRewardsConfig();
    if (!config) return;

    const cart = await fetchCart();
    const subtotal = getQualifyingSubtotal(cart, config);
    const giftLines = getGiftLineItems(cart, config);

    if (subtotal >= config.giftThresholdCents) {
      if (giftLines.length) {
        await normalizeGiftLines(cart, config);
      } else {
        await addGift(config);
      }
    } else {
      const removedFreeGift = await removeFreeGiftLines(cart, config);
      await normalizeGiftLines(removedFreeGift ? await fetchCart() : cart, config);
    }
  } catch (error) {
    console.error('Unable to sync cart rewards gift', error);
  } finally {
    syncInFlight = false;

    if (syncQueued) {
      syncQueued = false;
      scheduleSync();
    }
  }
}

function scheduleSync() {
  window.setTimeout(syncRewards, 0);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleSync);
window.addEventListener('load', scheduleSync);
